// src/components/ChatInterface.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Bot, User, Sparkles, Zap, Brain, Clock, Copy, ThumbsUp, ThumbsDown, ChevronRight, Search, X, ExternalLink, ChevronDown, Play } from 'lucide-react';
import { analyzeTeamRebalancing } from '../algorithms/teamRebalancing';
import { diagnoseBottlenecks } from '../algorithms/bottleneckDiagnosis';
import { planInitiative } from '../algorithms/initiativePlanner';
import { simulateBudgetImpact } from '../algorithms/budgetSimulator';
import { analyzeAICodingImpact } from '../algorithms/aiCodingImpact';
import { mockPeople, mockProjects } from '../data/mockData';
import DetailedAnalysis from './DetailedAnalysis';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  type: 'text' | 'analysis' | 'error';
  analysisData?: any;
  isLoading?: boolean;
}

interface ChatInterfaceProps {
  selectedCapability: string | null;
  onPromptSelect: (prompt: string) => void;
  initialInput: string;
  onInputChange: (input: string) => void;
  onViewDetailedAnalysis?: (analysisData: any) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  selectedCapability,
  onPromptSelect,
  initialInput,
  onInputChange,
  onViewDetailedAnalysis
}) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: "Hello! I'm your AI Chief of Staff. I can help you analyze team dynamics, diagnose bottlenecks, plan initiatives, simulate budget impacts, and measure AI coding tool effectiveness. What would you like to explore?",
      timestamp: new Date(),
      type: 'text'
    }
  ]);
  const [inputValue, setInputValue] = useState(initialInput);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([]);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState<any>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [autoStartWorkflow, setAutoStartWorkflow] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Example prompts based on selected capability - UPDATED WITH NEW CAPABILITY
  const capabilityPrompts = {
    'team-rebalancing': [
      "What happens if we move Sarah from Payments to AI Fraud Detection?",
      "Find a replacement for Alex who's leaving the Authentication team",
      "Simulate moving James to Cloud Migration project",
      "Who should we move to fix the Payments project delays?"
    ],
    'bottleneck-diagnosis': [
      "Why is the Payments project delayed?",
      "Identify blockers in Cloud Migration",
      "Analyze why AI Fraud Detection is slowing down",
      "What's causing the Security Enhancement delays?"
    ],
    'initiative-planner': [
      "Plan a new AI integration project",
      "Assemble team for mobile app redesign",
      "Start a new machine learning initiative",
      "Plan cross-platform development team"
    ],
    'budget-simulator': [
      "Simulate 10% budget cut on Engineering",
      "What if we increase Cloud budget by 20%?",
      "Reallocate marketing budget to product",
      "Simulate hiring freeze impact on projects"
    ],
    'ai-coding-impact': [  // NEW CAPABILITY PROMPTS
      "What's the productivity impact of AI coding tools?",
      "Compare project metrics with and without AI assistants",
      "Analyze token usage vs. code quality trade-offs",
      "Should we expand AI tool licenses to more teams?",
      "What's the ROI on our AI coding assistant investment?",
      "How does AI impact developer productivity by skill level?"
    ],
    'general': [
      "What happens if we move Sarah from Payments to AI Fraud Detection?",
      "Simulate 10% budget cut on Engineering",
      "Why is the Payments project delayed?",
      "Plan a new AI integration project",
      "What's the ROI on our AI coding tools?"
    ]
  };

  // Update suggested prompts when capability changes
  useEffect(() => {
    if (selectedCapability && capabilityPrompts[selectedCapability as keyof typeof capabilityPrompts]) {
      setSuggestedPrompts(capabilityPrompts[selectedCapability as keyof typeof capabilityPrompts]);
    } else {
      setSuggestedPrompts(capabilityPrompts.general);
    }
  }, [selectedCapability]);

  // Handle scroll position
  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setShowScrollToBottom(!isAtBottom);
    }
  }, []);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll to top of new response when it's added
  const scrollToNewResponse = useCallback(() => {
    if (messagesContainerRef.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && !lastMessage.isLoading) {
        // Find the assistant message element
        const messageElements = messagesContainerRef.current.querySelectorAll('.message-container');
        if (messageElements.length > 0) {
          const lastMessageElement = messageElements[messageElements.length - 1] as HTMLElement;
          if (lastMessageElement) {
            messagesContainerRef.current.scrollTo({
              top: lastMessageElement.offsetTop - 20,
              behavior: 'smooth'
            });
          }
        }
      }
    }
  }, [messages]);

  // Initialize scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Scroll to new response when messages change
  useEffect(() => {
    if (messages.length > 1) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && !lastMessage.isLoading) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          scrollToNewResponse();
        }, 100);
      }
    }
  }, [messages, scrollToNewResponse]);

  // Update local input when prop changes
  useEffect(() => {
    setInputValue(initialInput);
  }, [initialInput]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);
    onInputChange(value);
  };

  // Analyze user message and determine capability - UPDATED WITH NEW DETECTION
  const detectCapabilityFromMessage = (message: string): string | null => {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('move') || lowerMessage.includes('rebalance') || 
        lowerMessage.includes('replace') || lowerMessage.includes('transfer')) {
      return 'team-rebalancing';
    } else if (lowerMessage.includes('delay') || lowerMessage.includes('block') || 
               lowerMessage.includes('bottleneck') || lowerMessage.includes('slow')) {
      return 'bottleneck-diagnosis';
    } else if (lowerMessage.includes('plan') || lowerMessage.includes('initiative') || 
               lowerMessage.includes('assemble') || lowerMessage.includes('new project')) {
      return 'initiative-planner';
    } else if (lowerMessage.includes('budget') || lowerMessage.includes('cost') || 
               lowerMessage.includes('financial') || lowerMessage.includes('cut')) {
      return 'budget-simulator';
    } else if (lowerMessage.includes('ai coding') || 
               lowerMessage.includes('copilot') || 
               lowerMessage.includes('assistant impact') ||
               lowerMessage.includes('token usage') ||
               lowerMessage.includes('productivity impact') ||
               (lowerMessage.includes('code quality') && lowerMessage.includes('ai')) ||
               lowerMessage.includes('roi') && lowerMessage.includes('ai') ||
               lowerMessage.includes('generative ai') ||
               lowerMessage.includes('ai tool')) {
      return 'ai-coding-impact';
    }
    return null;
  };

  // Replace the entire processAnalysis function in ChatInterface.tsx with this:
  const processAnalysis = (capability: string, message: string): any => {
    try {
      console.log(`Processing ${capability} for message: ${message}`);
      
      // Enhanced parameter extraction for all capabilities
      const extractedParams = extractParametersFromMessage(message);
      
      switch (capability) {
        case 'team-rebalancing':
          return processTeamRebalancingAnalysis(message, extractedParams);
            
        case 'bottleneck-diagnosis':
          return processBottleneckDiagnosis(message, extractedParams);
            
        case 'initiative-planner':
          return processInitiativePlanning(message, extractedParams);
            
        case 'budget-simulator':
          return processBudgetSimulation(message, extractedParams);
            
        case 'ai-coding-impact':  // NEW CAPABILITY CASE
          return processAICodingImpactAnalysis(message, extractedParams);
            
        default:
          console.warn(`Unknown capability: ${capability}`);
          return generateGeneralAnalysis(message);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      return generateErrorAnalysis(message, capability, error);
    }
  };

  // Helper: Extract parameters from message with enhanced logic
  const extractParametersFromMessage = (message: string): any => {
    const params: any = {
      people: [],
      projects: [],
      percentages: [],
      timelines: [],
      skills: [],
      budgets: [],
      departments: [],
      constraints: []
    };
    
    const lowerMessage = message.toLowerCase();
    
    // Extract person names
    const personPatterns = [
      /(?:move|reassign|transfer|replace)\s+(\w+)/i,
      /(?:moving|reassigning|transferring)\s+(\w+)/i,
      /\b(\w+)\s+(?:from|to)\s+(?:project|team)/i
    ];
    
    for (const pattern of personPatterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        const person = mockPeople.find(p => 
          p.name.toLowerCase().includes(match[1].toLowerCase()) ||
          match[1].toLowerCase().includes(p.name.toLowerCase())
        );
        if (person) params.people.push(person);
      }
    }
    
    // Extract project names
    for (const project of mockProjects) {
      if (lowerMessage.includes(project.name.toLowerCase())) {
        params.projects.push(project);
      }
    }
    
    // Extract percentages
    const percentageMatches = message.match(/(\d+(?:\.\d+)?)\s*%/g);
    if (percentageMatches) {
      params.percentages = percentageMatches.map(p => parseFloat(p));
    }
    
    // Extract timelines
    const timelineMatches = message.match(/(\d+)\s*(?:week|month|day)s?/gi);
    if (timelineMatches) {
      params.timelines = timelineMatches.map(t => {
        const num = parseInt(t);
        if (t.toLowerCase().includes('week')) return { value: num, unit: 'weeks' };
        if (t.toLowerCase().includes('month')) return { value: num, unit: 'months' };
        return { value: num, unit: 'days' };
      });
    }
    
    // Extract budget amounts
    const budgetMatches = message.match(/\$?(\d+(?:,\d+)*(?:\.\d+)?)\s*(?:k|K|thousand|m|M|million)?/gi);
    if (budgetMatches) {
      params.budgets = budgetMatches.map(b => {
        const num = parseFloat(b.replace(/[^\d.]/g, ''));
        if (b.toLowerCase().includes('k') || b.toLowerCase().includes('thousand')) return num * 1000;
        if (b.toLowerCase().includes('m') || b.toLowerCase().includes('million')) return num * 1000000;
        return num;
      });
    }
    
    // Extract departments
    const departments = ['engineering', 'product', 'marketing', 'sales', 'operations', 'r&d', 'hr'];
    for (const dept of departments) {
      if (lowerMessage.includes(dept)) {
        params.departments.push(dept);
      }
    }
    
    // Extract constraints
    const constraintKeywords = [
      { keyword: 'no layoff', constraint: 'no layoffs' },
      { keyword: 'no firing', constraint: 'no layoffs' },
      { keyword: 'customer experience', constraint: 'maintain customer experience' },
      { keyword: 'quality', constraint: 'maintain quality standards' },
      { keyword: 'training', constraint: 'preserve training budget' },
      { keyword: 'bonus', constraint: 'protect bonuses' }
    ];
    
    for (const { keyword, constraint } of constraintKeywords) {
      if (lowerMessage.includes(keyword)) {
        params.constraints.push(constraint);
      }
    }
    
    return params;
  };

  // Helper: Enhanced generate capacity plan analysis
  const processTeamRebalancingAnalysis = (message: string, params: any) => {
    console.log("Processing generate capacity plan with params:", params);
    
    // Find person (priority: extracted > mentioned in message > default)
    let person = params.people[0];
    if (!person) {
      // Try to find person by name in message
      for (const p of mockPeople) {
        if (message.toLowerCase().includes(p.name.toLowerCase())) {
          person = p;
          break;
        }
      }
    }
    
    if (!person) person = mockPeople[0];
    
    // Find source and target projects
    let sourceProject = params.projects[0];
    let targetProject = params.projects[1];
    
    // If not enough projects from extraction, use intelligent defaults
    if (!sourceProject) {
      // Find project where person currently works
      sourceProject = mockProjects.find(p => 
        p.teamMembers?.includes(person.id)
      );
    }
    
    if (!targetProject) {
      // Find a different project than source
      targetProject = mockProjects.find(p => 
        p.id !== sourceProject?.id && 
        p.status !== 'red' // Prefer non-critical projects
      );
    }
    
    if (!sourceProject) sourceProject = mockProjects[0];
    if (!targetProject) targetProject = mockProjects[1];
    
    // Determine timeline based on message
    let timeline: 'immediate' | 'phased' | 'delayed' = 'phased';
    if (message.toLowerCase().includes('immediately') || message.toLowerCase().includes('right away')) {
      timeline = 'immediate';
    } else if (message.toLowerCase().includes('delay') || message.toLowerCase().includes('later')) {
      timeline = 'delayed';
    }
    
    // Use any extracted timeline information
    if (params.timelines.length > 0) {
      const timelineParam = params.timelines[0];
      if (timelineParam.unit === 'days' && timelineParam.value <= 3) {
        timeline = 'immediate';
      } else if (timelineParam.unit === 'weeks' && timelineParam.value > 4) {
        timeline = 'delayed';
      }
    }
    
    console.log(`Moving ${person.name} from ${sourceProject.name} to ${targetProject.name} (${timeline})`);
    
    // Call the algorithm
    const algorithmInput = {
      personId: person.id,
      fromProjectId: sourceProject.id,
      toProjectId: targetProject.id,
      transitionTimeline: timeline
    };
    
    const algorithmOutput = analyzeTeamRebalancing(algorithmInput);
    
    // Enhanced data for better response
    const enhancedData = {
      summary: `Generate Capacity Plan Analysis: Moving ${person.name}`,
      keyFindings: generateEnhancedFindings(algorithmOutput, 'team-rebalancing', {
        person,
        sourceProject,
        targetProject,
        timeline
      }),
      recommendations: algorithmOutput.recommendations?.map((rec: any, i: number) => 
        `${i + 1}. ${rec.action}`
      ) || [],
      workflow: generateWorkflowSteps('team-rebalancing', {
        timeline,
        personName: person.name,
        sourceProject: sourceProject.name,
        targetProject: targetProject.name
      }),
      successProbability: algorithmOutput.riskAssessment?.overallRiskScore ? 
        100 - algorithmOutput.riskAssessment.overallRiskScore : 75,
      confidenceScore: calculateConfidenceScore(algorithmOutput),
      quickActions: [
        'Notify team leads',
        'Schedule knowledge transfer',
        'Update HR systems',
        'Monitor transition'
      ]
    };
    
    return {
      ...algorithmOutput,
      input: algorithmInput,
      metadata: {
        capability: 'team-rebalancing',
        personName: person.name,
        fromProjectName: sourceProject.name,
        toProjectName: targetProject.name,
        timeline,
        extractedFromMessage: message,
        timestamp: new Date().toISOString()
      },
      enhancedData
    };
  };

  // Helper: Enhanced automate root cause analysis
  const processBottleneckDiagnosis = (message: string, params: any) => {
    console.log("Processing automate root cause analysis with params:", params);
    
    // Find project (priority: extracted > mentioned > problematic > default)
    let project = params.projects[0];
    
    if (!project) {
      // Look for project mentions in message
      for (const p of mockProjects) {
        if (message.toLowerCase().includes(p.name.toLowerCase())) {
          project = p;
          break;
        }
      }
    }
    
    if (!project) {
      // Find project with issues (red status or high delay)
      project = mockProjects.find(p => p.status === 'red' || (p.timelineDelay || 0) > 2);
    }
    
    if (!project) project = mockProjects[0];
    
    console.log(`Analyzing bottlenecks for project: ${project.name}`);
    
    // Call the algorithm
    const algorithmInput = {
      projectId: project.id
    };
    
    const algorithmOutput = diagnoseBottlenecks(algorithmInput);
    
    // Enhanced data
    const enhancedData = {
      summary: `Automate Root Cause Analysis: ${project.name}`,
      keyFindings: generateEnhancedFindings(algorithmOutput, 'bottleneck-diagnosis', {
        project,
        primaryCause: algorithmOutput.rootCauseAnalysis?.primaryCause
      }),
      recommendations: algorithmOutput.recommendations?.map((rec: any, i: number) => 
        `${i + 1}. ${rec.action}`
      ) || [],
      workflow: generateWorkflowSteps('bottleneck-diagnosis', {
        delayWeeks: algorithmOutput.impactAssessment?.timelineDelayWeeks || 3
      }),
      estimatedRecoveryTime: `${algorithmOutput.impactAssessment?.timelineDelayWeeks || 3} weeks`,
      confidenceScore: algorithmOutput.rootCauseAnalysis?.confidenceScore || 88,
      quickActions: [
        'Reallocate resources',
        'Schedule daily standups',
        'Create escalation channel',
        'Update project status'
      ]
    };
    
    return {
      ...algorithmOutput,
      input: algorithmInput,
      metadata: {
        capability: 'bottleneck-diagnosis',
        projectName: project.name,
        projectStatus: project.status,
        extractedFromMessage: message,
        timestamp: new Date().toISOString()
      },
      enhancedData
    };
  };

  // Helper: Enhanced initiative planning
  const processInitiativePlanning = (message: string, params: any) => {
    console.log("Processing initiative planning with params:", params);
    
    // Determine initiative type
    let initiativeType = 'Strategic Initiative';
    const initiativeKeywords = {
      'AI': /ai|artificial intelligence|machine learning|ml|fraud detection/i,
      'Mobile': /mobile|app|application|ios|android/i,
      'Cloud': /cloud|infrastructure|migration|aws|azure|gcp/i,
      'Security': /security|cybersecurity|authentication|encryption/i,
      'Web': /web|platform|portal|website|e-commerce/i
    };
    
    for (const [type, pattern] of Object.entries(initiativeKeywords)) {
      if (pattern.test(message)) {
        initiativeType = `${type} Initiative`;
        break;
      }
    }
    
    // Extract timeline (months)
    let timelineMonths = 6; // default
    if (params.timelines.length > 0) {
      const timeline = params.timelines[0];
      if (timeline.unit === 'months') {
        timelineMonths = timeline.value;
      } else if (timeline.unit === 'weeks') {
        timelineMonths = Math.ceil(timeline.value / 4);
      }
    }
    
    // Extract budget
    let budget = 500000; // default
    if (params.budgets.length > 0) {
      budget = params.budgets[0];
    } else {
      // Estimate budget based on initiative type
      const budgetMap: Record<string, number> = {
        'AI': 750000,
        'Mobile': 300000,
        'Cloud': 600000,
        'Security': 450000,
        'Web': 250000
      };
      
      for (const [type, typeBudget] of Object.entries(budgetMap)) {
        if (initiativeType.includes(type)) {
          budget = typeBudget;
          break;
        }
      }
    }
    
    // Determine required skills based on initiative type
    const requiredSkills = getRequiredSkillsForInitiative(initiativeType);
    
    console.log(`Planning ${initiativeType} with ${timelineMonths} month timeline and $${budget.toLocaleString()} budget`);
    
    // Call the algorithm
    const algorithmInput = {
      initiativeName: initiativeType,
      description: `Based on request: ${message.substring(0, 100)}...`,
      timelineMonths: Math.min(Math.max(timelineMonths, 3), 24),
      budget: Math.min(Math.max(budget, 100000), 5000000),
      requiredSkills
    };
    
    const algorithmOutput = planInitiative(algorithmInput);
    
    // Enhanced data
    const enhancedData = {
      summary: `Initiative Planning: ${initiativeType}`,
      keyFindings: generateEnhancedFindings(algorithmOutput, 'initiative-planner', {
        timelineMonths,
        budget,
        teamSize: algorithmOutput.teamComposition?.recommendedTeam?.length
      }),
      recommendations: algorithmOutput.recommendations?.map((rec: any, i: number) => 
        `${i + 1}. ${rec.action}`
      ) || [],
      workflow: generateWorkflowSteps('initiative-planner', {
        timelineMonths,
        teamSize: algorithmOutput.teamComposition?.recommendedTeam?.length
      }),
      successProbability: algorithmOutput.successMetrics?.probability || 75,
      confidenceScore: 85,
      quickActions: [
        'Assemble team',
        'Create project',
        'Schedule kickoff',
        'Allocate budget'
      ]
    };
    
    return {
      ...algorithmOutput,
      input: algorithmInput,
      metadata: {
        capability: 'initiative-planner',
        initiativeType,
        extractedFromMessage: message,
        timestamp: new Date().toISOString()
      },
      enhancedData
    };
  };

  // Helper: Enhanced budget simulation
  const processBudgetSimulation = (message: string, params: any) => {
    console.log("Processing budget simulation with params:", params);
    
    // Determine scenario
    let scenario: 'cut' | 'increase' | 'reallocate' = 'cut';
    if (message.toLowerCase().includes('increase') || message.toLowerCase().includes('add') || message.toLowerCase().includes('boost')) {
      scenario = 'increase';
    } else if (message.toLowerCase().includes('reallocate') || message.toLowerCase().includes('reallocation') || message.toLowerCase().includes('redistribute')) {
      scenario = 'reallocate';
    }
    
    // Extract percentage
    let percentage = 10; // default
    if (params.percentages.length > 0) {
      percentage = params.percentages[0];
    }
    
    // Extract department
    let department: 'engineering' | 'product' | 'marketing' | 'all' = 'engineering';
    if (params.departments.length > 0) {
      const dept = params.departments[0];
      if (['engineering', 'product', 'marketing'].includes(dept)) {
        department = dept as any;
      } else {
        department = 'all';
      }
    }
    
    // Extract constraints or use defaults
    let constraints = params.constraints;
    if (constraints.length === 0) {
      constraints = ['no layoffs', 'maintain customer experience'];
    }
    
    console.log(`Simulating ${scenario}: ${percentage}% ${scenario} for ${department}`);
    
    // Call the algorithm
    const algorithmInput = {
      scenario,
      percentage: Math.min(Math.max(percentage, 1), 50),
      department,
      constraints
    };
    
    const algorithmOutput = simulateBudgetImpact(algorithmInput);
    
    // Enhanced data
    const enhancedData = {
      summary: `Budget Simulation: ${percentage}% ${scenario} for ${department}`,
      keyFindings: generateEnhancedFindings(algorithmOutput, 'budget-simulator', {
        scenario,
        percentage,
        department,
        totalImpact: algorithmOutput.scenarioAnalysis?.totalImpact
      }),
      recommendations: algorithmOutput.recommendations?.map((rec: any, i: number) => 
        `${i + 1}. ${rec.action}`
      ) || [],
      workflow: generateWorkflowSteps('budget-simulator', {
        scenario,
        percentage,
        department
      }),
      confidenceScore: algorithmOutput.strategicImpact?.alignmentScore ? 
        Math.min(95, algorithmOutput.strategicImpact.alignmentScore + 10) : 90,
      quickActions: [
        'Freeze hiring',
        'Adjust budgets',
        'Notify stakeholders',
        'Monitor spending'
      ]
    };
    
    return {
      ...algorithmOutput,
      input: algorithmInput,
      metadata: {
        capability: 'budget-simulator',
        scenario,
        percentage,
        department,
        constraints,
        extractedFromMessage: message,
        timestamp: new Date().toISOString()
      },
      enhancedData
    };
  };

  // NEW: Helper for AI Coding Impact analysis
  const processAICodingImpactAnalysis = (message: string, params: any) => {
    console.log("Processing AI coding impact analysis with params:", params);
    
    // Determine timeframe
    let timeframe: '1-month' | '3-months' | '6-months' | 'year' = '3-months';
    if (message.toLowerCase().includes('1 month') || message.toLowerCase().includes('last month') || 
        message.toLowerCase().includes('monthly')) {
      timeframe = '1-month';
    } else if (message.toLowerCase().includes('6 month') || message.toLowerCase().includes('half year') || 
               message.toLowerCase().includes('six month')) {
      timeframe = '6-months';
    } else if (message.toLowerCase().includes('year') || message.toLowerCase().includes('annual') || 
               message.toLowerCase().includes('yearly')) {
      timeframe = 'year';
    }
    
    // Determine focus areas
    const metrics: Array<'productivity' | 'quality' | 'velocity' | 'token-usage'> = [];
    if (message.toLowerCase().includes('productivity') || message.toLowerCase().includes('speed') || 
        message.toLowerCase().includes('efficiency')) {
      metrics.push('productivity');
    }
    if (message.toLowerCase().includes('quality') || message.toLowerCase().includes('defect') || 
        message.toLowerCase().includes('bug')) {
      metrics.push('quality');
    }
    if (message.toLowerCase().includes('velocity') || message.toLowerCase().includes('throughput')) {
      metrics.push('velocity');
    }
    if (message.toLowerCase().includes('token') || message.toLowerCase().includes('cost') || 
        message.toLowerCase().includes('usage')) {
      metrics.push('token-usage');
    }
    
    // Use all metrics if none specified
    if (metrics.length === 0) {
      metrics.push('productivity', 'quality', 'velocity', 'token-usage');
    }
    
    // Get relevant projects
    const relevantProjects = params.projects.length > 0 
      ? params.projects.slice(0, 3).map((p: any) => p.id)
      : ['PROJ_001', 'PROJ_002']; // Default to Payments and AI Fraud Detection
    
    console.log(`Analyzing AI coding impact for timeframe: ${timeframe}, metrics: ${metrics.join(', ')}`);
    
    // Call the algorithm
    const algorithmInput = {
      projectIds: relevantProjects,
      timeframe,
      metrics,
      comparison: 'with-vs-without'
    };
    
    const algorithmOutput = analyzeAICodingImpact(algorithmInput);
    
    // Enhanced data for chat response
    const enhancedData = {
      summary: `Generate AI Coding Impact Analysis`,
      keyFindings: generateEnhancedFindings(algorithmOutput, 'ai-coding-impact', {
        overallImpact: algorithmOutput.summary?.overallImpact,
        productivityGain: algorithmOutput.productivityAnalysis?.completionTimeImprovement,
        qualityImprovement: algorithmOutput.qualityMetrics?.defectRate ? 
          ((algorithmOutput.qualityMetrics.defectRate.withoutAI - 
            algorithmOutput.qualityMetrics.defectRate.withAI) / 
            algorithmOutput.qualityMetrics.defectRate.withoutAI * 100).toFixed(1) : '25',
        roi: algorithmOutput.tokenUsage?.roi
      }),
      recommendations: algorithmOutput.recommendations?.map((rec: any, i: number) => 
        `${i + 1}. ${rec.action} (${rec.priority} priority)`
      ) || [],
      workflow: generateWorkflowSteps('ai-coding-impact', {
        recommendation: algorithmOutput.summary?.recommendation,
        adoptionRate: algorithmOutput.teamImpact?.adoptionRate
      }),
      successProbability: algorithmOutput.summary?.overallImpact ? 
        Math.min(100, 50 + algorithmOutput.summary.overallImpact) : 75,
      confidenceScore: algorithmOutput.summary?.confidence || 87,
      quickActions: [
        'Generate detailed report',
        'Schedule team training',
        'Adjust license allocation',
        'Set up monitoring dashboard'
      ]
    };
    
    return {
      ...algorithmOutput,
      input: algorithmInput,
      metadata: {
        capability: 'ai-coding-impact',
        extractedFromMessage: message,
        timeframe,
        timestamp: new Date().toISOString()
      },
      enhancedData
    };
  };

  // Helper: Generate enhanced findings based on algorithm output - UPDATED WITH NEW CAPABILITY
  const generateEnhancedFindings = (output: any, capability: string, context: any): string[] => {
    const findings: string[] = [];
    
    switch (capability) {
      case 'team-rebalancing':
        if (output.riskAssessment?.overallRiskScore) {
          findings.push(`Risk score: ${output.riskAssessment.overallRiskScore}/100`);
        }
        if (output.riskAssessment?.timelineImpact) {
          findings.push(`Timeline impact: ${output.riskAssessment.timelineImpact.sourceProjectDelayWeeks} weeks delay for source project`);
        }
        if (output.visualizationData?.skillGaps?.length > 0) {
          const avgGap = output.visualizationData.skillGaps.reduce((sum: number, gap: any) => sum + gap.gap, 0) / output.visualizationData.skillGaps.length;
          findings.push(`Average skill gap: ${avgGap.toFixed(1)} points`);
        }
        break;
        
      case 'bottleneck-diagnosis':
        if (output.rootCauseAnalysis?.primaryCause) {
          findings.push(`Primary cause: ${output.rootCauseAnalysis.primaryCause.description}`);
        }
        if (output.impactAssessment?.timelineDelayWeeks) {
          findings.push(`Timeline delay: ${output.impactAssessment.timelineDelayWeeks} weeks`);
        }
        if (output.impactAssessment?.financialImpact) {
          findings.push(`Financial impact: $${output.impactAssessment.financialImpact.toLocaleString()}`);
        }
        break;
        
      case 'initiative-planner':
        if (output.teamComposition?.recommendedTeam) {
          findings.push(`Recommended team size: ${output.teamComposition.recommendedTeam.length} people`);
        }
        if (output.teamComposition?.totalCost) {
          findings.push(`Estimated annual cost: $${output.teamComposition.totalCost.toLocaleString()}`);
        }
        if (output.timelineAnalysis?.optimalTimeline) {
          findings.push(`Optimal timeline: ${output.timelineAnalysis.optimalTimeline} months`);
        }
        if (output.successMetrics?.expectedROI) {
          findings.push(`Expected ROI: $${output.successMetrics.expectedROI.toLocaleString()}`);
        }
        break;
        
      case 'budget-simulator':
        if (output.scenarioAnalysis?.totalImpact) {
          const impact = output.scenarioAnalysis.totalImpact;
          findings.push(`Total financial impact: $${Math.abs(impact).toLocaleString()} ${impact >= 0 ? 'increase' : 'reduction'}`);
        }
        if (output.strategicImpact?.alignmentScore) {
          findings.push(`Strategic alignment: ${output.strategicImpact.alignmentScore}%`);
        }
        if (output.implementationPlan?.totalSavings) {
          findings.push(`Total savings: $${output.implementationPlan.totalSavings.toLocaleString()}`);
        }
        if (output.implementationPlan?.timelineMonths) {
          findings.push(`Implementation timeline: ${output.implementationPlan.timelineMonths} months`);
        }
        break;
        
      case 'ai-coding-impact':  // NEW FINDINGS FOR AI CODING IMPACT
        if (output.summary?.overallImpact) {
          findings.push(`Overall productivity impact: +${output.summary.overallImpact}%`);
        }
        if (output.productivityAnalysis?.completionTimeImprovement) {
          findings.push(`Task completion time improved: ${output.productivityAnalysis.completionTimeImprovement}%`);
        }
        if (output.qualityMetrics?.defectRate) {
          findings.push(`Defect rate reduction: ${((output.qualityMetrics.defectRate.withoutAI - output.qualityMetrics.defectRate.withAI) / output.qualityMetrics.defectRate.withoutAI * 100).toFixed(1)}%`);
        }
        if (output.tokenUsage?.roi) {
          findings.push(`ROI on AI tools: ${output.tokenUsage.roi}%`);
        }
        if (output.teamImpact?.adoptionRate) {
          findings.push(`Team adoption rate: ${output.teamImpact.adoptionRate}%`);
        }
        break;
    }
    
    return findings;
  };

  // Helper: Generate workflow steps - UPDATED WITH NEW CAPABILITY
  const generateWorkflowSteps = (capability: string, context: any): string[] => {
    switch (capability) {
      case 'team-rebalancing':
        const timeline = context.timeline || 'phased';
        if (timeline === 'immediate') {
          return [
            'Day 1: Announcement and immediate handover',
            'Day 2: System access updates and provisioning',
            'Day 3: Team introductions and onboarding',
            'Week 1: Intensive knowledge transfer sessions',
            'Week 2: Full transition completion and review'
          ];
        } else if (timeline === 'delayed') {
          return [
            'Week 1: Planning and preparation phase',
            'Week 2-3: Gradual knowledge transfer',
            'Week 4: Partial handover begins',
            'Month 2: Full transition completion',
            'Month 3: Performance review and adjustment'
          ];
        } else { // phased
          return [
            'Week 1: Manager approvals and planning',
            'Week 2: Knowledge transfer sessions',
            'Week 3: System updates and access changes',
            'Week 4: Team communication and handover',
            'Month 2: Performance monitoring and support'
          ];
        }
        
      case 'bottleneck-diagnosis':
        const delayWeeks = context.delayWeeks || 3;
        return [
          'Day 1: Critical path analysis and triage',
          'Week 1: Resource reallocation and immediate fixes',
          `Week 2-${Math.ceil(delayWeeks/2)}: Process improvements and technical resolution`,
          `Week ${Math.ceil(delayWeeks/2)+1}-${delayWeeks}: Monitoring and stabilization`,
          'Ongoing: Prevention measures and continuous improvement'
        ];
        
      case 'initiative-planner':
        const timelineMonths = context.timelineMonths || 6;
        const teamSize = context.teamSize || 5;
        return [
          `Month 1: Requirements gathering (${teamSize} team members)`,
          `Month 2-${Math.floor(timelineMonths/2)}: Development sprints`,
          `Month ${Math.floor(timelineMonths/2)+1}-${timelineMonths-1}: Testing and refinement`,
          `Month ${timelineMonths}: Launch, monitoring, and handover`
        ];
        
      case 'budget-simulator':
        const scenario = context.scenario || 'cut';
        const percentage = context.percentage || 10;
        const department = context.department || 'engineering';
        return [
          'Week 1: Impact analysis and stakeholder identification',
          'Week 2: Department review meetings and feedback',
          'Week 3: Final adjustments and approval process',
          'Week 4: System updates and communication rollout',
          'Month 2: Monitoring, adjustment, and quarterly review'
        ];
        
      case 'ai-coding-impact':  // NEW WORKFLOW FOR AI CODING IMPACT
        const recommendation = context.recommendation || 'maintain';
        const adoptionRate = context.adoptionRate || 70;
        
        if (recommendation === 'expand') {
          return [
            'Week 1: Identify high-potential teams for expansion',
            'Week 2: Procurement and license allocation',
            'Week 3: Team training and onboarding sessions',
            'Week 4: Initial usage monitoring and support',
            'Month 2: Performance evaluation and adjustments'
          ];
        } else if (recommendation === 'investigate') {
          return [
            'Week 1: Deep dive analysis on underperforming teams',
            'Week 2: Interview developers and collect feedback',
            'Week 3: Identify patterns and root causes',
            'Week 4: Create improvement plan',
            'Month 2: Implement changes and monitor'
          ];
        } else {
          return [
            'Month 1: Regular usage monitoring and reporting',
            'Month 2: Optimization of token usage patterns',
            'Month 3: Update training materials based on learnings',
            'Quarterly: ROI review and license optimization'
          ];
        }
        
      default:
        return ['Analysis', 'Planning', 'Execution', 'Review'];
    }
  };

  // Helper: Calculate confidence score
  const calculateConfidenceScore = (output: any): number => {
    // Base confidence score
    let confidence = 85;
    
    // Adjust based on data completeness
    if (output.riskAssessment && output.recommendations && output.visualizationData) {
      confidence += 5;
    }
    
    // Adjust based on recommendation count
    if (output.recommendations && output.recommendations.length >= 3) {
      confidence += 5;
    }
    
    return Math.min(95, confidence);
  };

  // Helper: Get required skills for initiative
  const getRequiredSkillsForInitiative = (initiativeType: string) => {
    const skillSets: Record<string, Array<{ skill: string; level: number; headcount: number }>> = {
      'AI Initiative': [
        { skill: 'Python', level: 9, headcount: 3 },
        { skill: 'Machine Learning', level: 8, headcount: 2 },
        { skill: 'Data Engineering', level: 7, headcount: 2 },
        { skill: 'Cloud (AWS/Azure)', level: 8, headcount: 1 }
      ],
      'Mobile Initiative': [
        { skill: 'React Native', level: 8, headcount: 2 },
        { skill: 'iOS (Swift)', level: 7, headcount: 1 },
        { skill: 'Android (Kotlin)', level: 7, headcount: 1 },
        { skill: 'Backend API', level: 7, headcount: 1 }
      ],
      'Cloud Initiative': [
        { skill: 'AWS/Azure', level: 9, headcount: 2 },
        { skill: 'Terraform', level: 8, headcount: 1 },
        { skill: 'Kubernetes', level: 8, headcount: 1 },
        { skill: 'DevOps', level: 7, headcount: 1 }
      ],
      'Security Initiative': [
        { skill: 'Security', level: 9, headcount: 2 },
        { skill: 'Network Security', level: 8, headcount: 1 },
        { skill: 'Compliance', level: 7, headcount: 1 },
        { skill: 'Penetration Testing', level: 8, headcount: 1 }
      ],
      'Web Initiative': [
        { skill: 'React/Next.js', level: 8, headcount: 2 },
        { skill: 'Node.js', level: 8, headcount: 2 },
        { skill: 'Database Design', level: 7, headcount: 1 },
        { skill: 'DevOps', level: 7, headcount: 1 }
      ]
    };
    
    return skillSets[initiativeType] || [
      { skill: 'Full Stack', level: 8, headcount: 3 },
      { skill: 'Backend', level: 8, headcount: 2 },
      { skill: 'Frontend', level: 7, headcount: 2 },
      { skill: 'DevOps', level: 7, headcount: 1 }
    ];
  };

  // Helper: Generate general analysis for unknown capabilities
  const generateGeneralAnalysis = (message: string) => {
    return {
      input: { message },
      metadata: {
        capability: 'general',
        extractedFromMessage: message,
        timestamp: new Date().toISOString()
      },
      enhancedData: {
        summary: 'General Analysis',
        keyFindings: [
          'I analyzed your message for organizational insights',
          'For specific analyses, try mentioning team moves, project delays, new initiatives, or budget changes'
        ],
        recommendations: [
          'Try asking about generate capacity plan (e.g., "What happens if we move Sarah to AI team?")',
          'Ask about automate root cause analysis (e.g., "Why is Payments project delayed?")',
          'Request initiative planning (e.g., "Plan a new mobile app project")',
          'Simulate budget changes (e.g., "Simulate 10% budget cut on Engineering")',
          'Measure AI coding impact (e.g., "What\'s the ROI on our AI coding tools?")'
        ],
        confidenceScore: 70
      }
    };
  };

  // Helper: Generate error analysis
  const generateErrorAnalysis = (message: string, capability: string, error: any) => {
    return {
      input: { message, capability },
      error: error.message,
      metadata: {
        capability,
        extractedFromMessage: message,
        timestamp: new Date().toISOString(),
        status: 'error'
      },
      enhancedData: {
        summary: 'Analysis Error',
        keyFindings: [
          'I encountered an error while analyzing your request',
          'Please try rephrasing or ask a different question'
        ],
        recommendations: [
          'Check that your question includes specific details',
          'Try using simpler language',
          'Focus on one capability at a time'
        ],
        confidenceScore: 0
      }
    };
  };

  // Helper functions for data generation
  const determineTransitionTimeline = (message: string): 'immediate' | 'phased' | 'delayed' => {
    if (message.toLowerCase().includes('immediate') || message.toLowerCase().includes('now')) {
      return 'immediate';
    } else if (message.toLowerCase().includes('delayed') || message.toLowerCase().includes('later')) {
      return 'delayed';
    }
    return 'phased';
  };

  const generateTeamRebalancingFindings = (output: any, person: any, fromProject: any, toProject: any): string[] => {
    const findings: string[] = [];
    
    if (output.riskAssessment?.overallRiskScore) {
      findings.push(`Risk score: ${output.riskAssessment.overallRiskScore}/100`);
    }
    
    if (output.riskAssessment?.timelineImpact) {
      findings.push(`Timeline impact: ${output.riskAssessment.timelineImpact.sourceProjectDelayWeeks} weeks delay for source project`);
    }
    
    if (output.visualizationData?.skillGaps) {
      const avgGap = output.visualizationData.skillGaps.reduce((sum: number, gap: any) => sum + gap.gap, 0) / output.visualizationData.skillGaps.length;
      findings.push(`Average skill gap: ${avgGap.toFixed(1)} points`);
    }
    
    if (output.teamComposition?.skillCoverage) {
      findings.push(`Skill coverage: ${output.teamComposition.skillCoverage}% match with required skills`);
    }
    
    return findings;
  };

  const generateTeamRebalancingWorkflow = (timeline: string): string[] => {
    switch (timeline) {
      case 'immediate':
        return [
          'Day 1: Announcement and handover initiation',
          'Day 2: Access provisioning and system updates',
          'Day 3: Team introduction and onboarding',
          'Week 1: Initial project familiarization',
          'Week 2: Full transition completion'
        ];
      case 'phased':
        return [
          'Week 1: Knowledge transfer planning',
          'Week 2: Overlap period with current role',
          'Week 3: Gradual handover of responsibilities',
          'Week 4: Full transition to new role',
          'Month 2: Performance review and adjustment'
        ];
      default: // delayed
        return [
          'Month 1: Preparation and planning',
          'Month 2: Gradual knowledge transfer',
          'Month 3: Partial handover start',
          'Month 4: Full transition completion'
        ];
    }
  };

  const generateBottleneckFindings = (output: any, project: any): string[] => {
    const findings: string[] = [];
    
    if (output.rootCauseAnalysis?.primaryCause) {
      findings.push(`Primary cause: ${output.rootCauseAnalysis.primaryCause.description}`);
    }
    
    if (output.impactAssessment?.timelineDelayWeeks) {
      findings.push(`Timeline delay: ${output.impactAssessment.timelineDelayWeeks} weeks`);
    }
    
    if (output.impactAssessment?.financialImpact) {
      findings.push(`Financial impact: $${output.impactAssessment.financialImpact.toLocaleString()}`);
    }
    
    if (output.visualizationData?.velocityTrend) {
      const velocity = output.visualizationData.velocityTrend;
      if (velocity.length > 1) {
        const trend = velocity[velocity.length - 1].velocity - velocity[0].velocity;
        findings.push(`Velocity trend: ${trend > 0 ? '+' : ''}${trend.toFixed(1)} points`);
      }
    }
    
    return findings;
  };

  const generateBottleneckWorkflow = (recommendations: any[]): string[] => {
    const workflow = [
      'Immediate (Week 1): Critical path analysis and triage',
      'Short-term (Week 2): Resource reallocation and process adjustments',
      'Medium-term (Month 1): Technical debt resolution and skill development',
      'Long-term (Month 2-3): Prevention measures and monitoring implementation'
    ];
    
    return workflow;
  };

  const generateInitiativeFindings = (output: any): string[] => {
    const findings: string[] = [];
    
    if (output.teamComposition?.recommendedTeam) {
      findings.push(`Recommended team size: ${output.teamComposition.recommendedTeam.length} people`);
    }
    
    if (output.teamComposition?.totalCost) {
      findings.push(`Estimated annual cost: $${output.teamComposition.totalCost.toLocaleString()}`);
    }
    
    if (output.timelineAnalysis?.optimalTimeline) {
      findings.push(`Optimal timeline: ${output.timelineAnalysis.optimalTimeline} months`);
    }
    
    if (output.skillGapAnalysis?.overallGapScore) {
      findings.push(`Skill gap score: ${output.skillGapAnalysis.overallGapScore}/100`);
    }
    
    if (output.successMetrics?.expectedROI) {
      findings.push(`Expected ROI: $${output.successMetrics.expectedROI.toLocaleString()}`);
    }
    
    return findings;
  };

  const generateInitiativeWorkflow = (timelineMonths: number): string[] => {
    const phases = [
      `Month 1-2: Requirements gathering and team assembly`,
      `Month 3-${Math.floor(timelineMonths/2)}: Core development and implementation`,
      `Month ${Math.floor(timelineMonths/2)+1}-${timelineMonths-1}: Testing, refinement, and deployment`,
      `Month ${timelineMonths}: Launch and monitoring`
    ];
    
    return phases;
  };

  const generateBudgetFindings = (output: any, scenario: string, percentage: number): string[] => {
    const findings: string[] = [];
    
    if (output.scenarioAnalysis?.totalImpact) {
      const impact = output.scenarioAnalysis.totalImpact;
      findings.push(`Total financial impact: $${Math.abs(impact).toLocaleString()} ${impact >= 0 ? 'increase' : 'reduction'}`);
    }
    
    if (output.strategicImpact?.alignmentScore) {
      findings.push(`Strategic alignment: ${output.strategicImpact.alignmentScore}%`);
    }
    
    if (output.implementationPlan?.totalSavings) {
      findings.push(`Total savings: $${output.implementationPlan.totalSavings.toLocaleString()}`);
    }
    
    if (output.implementationPlan?.timelineMonths) {
      findings.push(`Implementation timeline: ${output.implementationPlan.timelineMonths} months`);
    }
    
    return findings;
  };

  const generateBudgetWorkflow = (phases: any[]): string[] => {
    return phases.map(phase => 
      `${phase.duration}: ${phase.actions.length} actions with $${phase.expectedSavings?.toLocaleString() || '0'} savings`
    );
  };

  // Generate enhanced response with workflow simulation note - UPDATED WITH NEW CAPABILITY
  const generateEnhancedResponse = (capability: string, analysis: any, userMessage: string): string => {
    if (!analysis || !analysis.enhancedData) {
      return "I couldn't analyze that request. Please try rephrasing or ask about generate capacity plan, automate root cause analysis, initiative planning, budget simulation, or AI coding impact.";
    }

    const { enhancedData, metadata } = analysis;
    
    // Get workflow steps based on capability
    const workflowSteps = getWorkflowStepsForCapability(capability, analysis);
    
    switch (capability) {
      case 'team-rebalancing':
        return `## 📊 Generate Capacity Plan Analysis
**Move:** ${metadata.personName || 'Employee'} from ${metadata.fromProjectName || 'Source Project'} to ${metadata.toProjectName || 'Target Project'}

### 🔍 Key Findings:
${enhancedData.keyFindings.map((finding: string) => `- ${finding}`).join('\n')}

### 💡 Recommendations:
${enhancedData.recommendations.map((rec: string, i: number) => `${i + 1}. ${rec}`).join('\n')}

### 🔄 Workflow Timeline:
${workflowSteps.map((step: string, i: number) => `${i + 1}. ${step}`).join('\n')}

🚀 **Try Workflow Simulation:** Click the "Run Workflow Simulation" button below to see how this transition would unfold step by step with real-time progress tracking.

### 🚀 Quick Actions Available:
• **Notify Team Leads** - Send automated notifications to managers
• **Schedule Meetings** - Book calendar invites for knowledge transfer
• **Update Systems** - Trigger HR and project management system updates
• **Monitor Progress** - Set up automated progress tracking

### 📈 Success Probability: ${enhancedData.successProbability}%
**Confidence Score:** ${enhancedData.confidenceScore}%

---

*Analysis based on organizational patterns and historical data.*`;

      case 'budget-simulator':
        // Extract personnel insights
    const personnelRecs = analysis.personnelImpact?.recommendations || [];
    const contractorRecs = personnelRecs.filter(r => r.justification.includes('Contractor'));
    const lowPerformerRecs = personnelRecs.filter(r => r.type === 'reduce' && r.justification.includes('Low performer'));
    const misalignedRecs = personnelRecs.filter(r => r.type === 'optimize' && r.justification.includes('misaligned'));
    const highPotentialRecs = personnelRecs.filter(r => r.justification.includes('High potential'));
    
    return `## 💰 Intelligent Budget Simulation
**Scenario:** ${metadata.percentage || 10}% ${metadata.scenario || 'cut'} for ${metadata.department || 'engineering'}

### 🔴 High-Impact Recommendations:
${
  contractorRecs.length > 0 ? `• **Contractor Optimization:** ${contractorRecs.length} contractors identified for adjustment\n` : ''
}${
  lowPerformerRecs.length > 0 ? `• **Performance Management:** ${lowPerformerRecs.length} low performers (${lowPerformerRecs.reduce((sum, r) => sum + r.impact, 0).toLocaleString()} impact)\n` : ''
}${
  misalignedRecs.length > 0 ? `• **Career Alignment:** ${misalignedRecs.length} high performers need career development\n` : ''
}${
  highPotentialRecs.length > 0 ? `• **High Potential:** ${highPotentialRecs.length} individuals ready for investment\n` : ''
}
### 📊 Impact Breakdown:
${enhancedData.keyFindings.map((finding: string) => `- ${finding}`).join('\n')}

${
  personnelRecs.length > 0 ? `
### 👥 Individual Insights:
${personnelRecs.slice(0, 3).map((rec: any) => 
  `• **${getPersonName(rec.personId)}:** ${rec.justification} (${rec.timeline})`
).join('\n')}
${personnelRecs.length > 3 ? `*... and ${personnelRecs.length - 3} more insights*` : ''}
` : ''
}

### ✅ Recommended Actions:
${enhancedData.recommendations.map((rec: string, i: number) => `${i + 1}. ${rec}`).join('\n')}

### 📅 Implementation Timeline:
${workflowSteps.map((step: string) => `- ${step}`).join('\n')}

### 🚀 Quick Actions Available:
• **Review Contractor Agreements** - Analyze and optimize contractor usage
• **Performance Improvement Plans** - Set up for identified low performers
• **Career Development Sessions** - Schedule for misaligned high performers
• **Update Budget Systems** - Apply financial adjustments

### 🎯 Strategic Considerations:
- **Personnel Impact:** ${analysis.personnelImpact?.headcountChange || 0} headcount change
- **Total Savings:** $${Math.abs(analysis.scenarioAnalysis?.totalImpact || 0).toLocaleString()}
- **Alignment Score:** ${analysis.strategicImpact?.alignmentScore || 85}%
- **Risks Mitigated:** ${analysis.strategicImpact?.risks?.filter((r: any) => r.severity === 'high').length || 0} high severity

### 📈 Confidence Level: ${enhancedData.confidenceScore}%

---

*Based on financial models, historical spending patterns, and industry benchmarks.*`;

      case 'initiative-planner':
        return `## 🎯 Initiative Planning
**Project:** ${metadata.initiativeType || 'Strategic Initiative'}

### 📋 Key Details:
${enhancedData.keyFindings.map((finding: string) => `- ${finding}`).join('\n')}

### 👥 Recommended Actions:
${enhancedData.recommendations.map((rec: string, i: number) => `${i + 1}. ${rec}`).join('\n')}

### 📅 Project Timeline:
${workflowSteps.map((step: string, i: number) => `${i + 1}. ${step}`).join('\n')}

🚀 **Try Workflow Simulation:** Click the "Run Workflow Simulation" button below to visualize the project progression from planning to deployment.

### 🚀 Quick Actions Available:
• **Assemble Team** - Send invites to recommended team members
• **Create Project** - Set up project in management tools
• **Schedule Kickoff** - Book kickoff meeting with stakeholders
• **Allocate Budget** - Reserve funds in finance system

### 💰 Budget & Resources:
- **Timeline:** ${analysis.input?.timelineMonths || 6} months
- **Budget:** $${analysis.input?.budget?.toLocaleString() || '500,000'}
- **Team Size:** ${analysis.output?.teamComposition?.recommendedTeam?.length || 7} members

### 📈 Success Metrics:
- **Probability:** ${enhancedData.successProbability}%
- **Confidence:** ${enhancedData.confidenceScore}%

---

*Recommendations based on skill availability, historical project data, and market trends.*`;

      case 'bottleneck-diagnosis':
        return `## 🚨 Automate Root Cause Analysis
**Project:** ${metadata.projectName || 'Target Project'}

### 🔍 Root Cause Analysis:
${enhancedData.keyFindings.map((finding: string) => `- ${finding}`).join('\n')}

### 🛠️ Resolution Plan:
${enhancedData.recommendations.map((rec: string, i: number) => `${i + 1}. ${rec}`).join('\n')}

### ⏱️ Recovery Timeline:
${workflowSteps.map((step: string, i: number) => `${i + 1}. ${step}`).join('\n')}

🚀 **Try Workflow Simulation:** Click the "Run Workflow Simulation" button below to see the resolution plan in action with estimated completion times.

### 🚀 Quick Actions Available:
• **Reallocate Resources** - Move team members to critical path
• **Schedule Reviews** - Book daily standups with stakeholders
• **Create Escalation** - Set up executive escalation channel
• **Update Status** - Change project status in tracking systems

### ⚡ Estimated Recovery: ${enhancedData.estimatedRecoveryTime}
**Confidence:** ${enhancedData.confidenceScore}% based on similar cases

---

*Analysis incorporates real-time project metrics and team performance data.*`;

      case 'ai-coding-impact':  // NEW RESPONSE FOR AI CODING IMPACT
        return `## 🤖 Generate AI Coding Impact Analysis

### 📊 Executive Summary
**Overall Impact:** +${analysis.summary?.overallImpact || 0}% productivity improvement
**Recommendation:** **${analysis.summary?.recommendation?.toUpperCase() || 'MAINTAIN'}** current AI tool usage

### 🚀 Key Findings:
${enhancedData.keyFindings.map((finding: string) => `- ${finding}`).join('\n')}

### 📈 Productivity Metrics:
- **Lines of Code/Hour:** ${analysis.productivityAnalysis?.linesOfCodePerHour?.withAI} with AI vs ${analysis.productivityAnalysis?.linesOfCodePerHour?.withoutAI} without
- **Completion Time:** ${analysis.productivityAnalysis?.completionTimeImprovement}% faster
- **Developer Sentiment:** ${analysis.productivityAnalysis?.developerSentiment || 'Positive'}

### 🎯 Quality Impact:
- **Defect Rate:** ${analysis.qualityMetrics?.defectRate?.withAI}% with AI vs ${analysis.qualityMetrics?.defectRate?.withoutAI}% without
- **Code Review Time:** ${analysis.qualityMetrics?.codeReviewTime?.withAI} with AI vs ${analysis.qualityMetrics?.codeReviewTime?.withoutAI} without
- **PR Size:** ${analysis.qualityMetrics?.pullRequestSize?.withAI} lines with AI vs ${analysis.qualityMetrics?.pullRequestSize?.withoutAI} without

### 💰 Cost Analysis:
- **Avg Daily Tokens:** ${analysis.tokenUsage?.averageTokensPerDay?.toLocaleString()}
- **Cost/Developer/Month:** $${analysis.tokenUsage?.costPerDeveloper}
- **ROI:** ${analysis.tokenUsage?.roi}%

### 👥 Team Impact:
**Adoption Rate:** ${analysis.teamImpact?.adoptionRate}%
**Learning Curve:** ${analysis.teamImpact?.learningCurve}

### 💡 Recommendations:
${enhancedData.recommendations.map((rec: string, i: number) => `${i + 1}. ${rec}`).join('\n')}

### 🔄 Recommended Actions:
1. **For Junior Developers:** Leverage AI for learning and boilerplate code
2. **For Senior Developers:** Use AI for repetitive tasks and code reviews
3. **Token Optimization:** ${analysis.tokenUsage?.optimizationOpportunities?.length || 0} patterns identified
4. **Monitoring:** Set up dashboard for ongoing impact tracking

### 🚀 Quick Actions Available:
• **Generate Detailed Report** - Export to PDF/Excel
• **Schedule Team Training** - Book sessions with identified teams
• **Adjust Licenses** - Optimize based on usage patterns
• **Create Dashboard** - Real-time monitoring setup

### 📊 Optimization Opportunities:
${
  analysis.tokenUsage?.optimizationOpportunities?.map((opp: any, i: number) => 
    `• **${opp.pattern}:** ${opp.impact}% impact - ${opp.recommendation}`
  ).join('\n') || 'No optimization opportunities identified'
}

### 📅 Recommended Timeline:
${workflowSteps.map((step: string, i: number) => `${i + 1}. ${step}`).join('\n')}

---

*Analysis based on ${analysis.input?.timeframe || '3-month'} data from ${analysis.input?.projectIds?.length || 2} projects.*`;

      default:
        return "I've analyzed your request. Here are the key findings...";
    }
  };

  function getPersonName(personId: string) {
  const person = mockPeople.find(p => p.id === personId);
  return person ? person.name : `Employee ${personId}`;
}
  // Helper function for workflow steps - UPDATED WITH NEW CAPABILITY
  const getWorkflowStepsForCapability = (capability: string, analysis: any): string[] => {
    switch (capability) {
      case 'team-rebalancing':
        return [
          'Day 1: Manager approvals and HR notification',
          'Day 2-3: Knowledge transfer planning and scheduling',
          'Week 1: System updates and access provisioning',
          'Week 2: Team communication and handover start',
          'Week 3-4: Gradual transition with overlap',
          'Month 2: Performance review and adjustment'
        ];
        
      case 'budget-simulator':
        const percentage = analysis.input?.percentage || 10;
        const scenario = analysis.input?.scenario || 'cut';
        return [
          'Week 1: Freeze non-essential spending and hiring',
          'Week 2: Department head meetings for feedback',
          'Week 3: Finalize budget adjustments',
          'Week 4: Update financial systems and allocations',
          'Month 2: Monitor spending and adjust as needed',
          'Month 3: Review impact and prepare quarterly report'
        ];
        
      case 'initiative-planner':
        const timeline = analysis.input?.timelineMonths || 6;
        return [
          'Month 1: Requirements gathering and team assembly',
          `Month 2-${Math.floor(timeline/2)}: Core development sprints`,
          `Month ${Math.floor(timeline/2)+1}-${timeline-1}: Testing and refinement`,
          `Month ${timeline}: Launch, monitoring, and handover`
        ];
        
      case 'bottleneck-diagnosis':
        const delay = analysis.impactAssessment?.timelineDelayWeeks || 3;
        return [
          'Day 1: Critical path analysis and triage meeting',
          'Week 1: Resource reallocation and process adjustments',
          `Week 2-${Math.ceil(delay/2)}: Technical resolution and monitoring`,
          `Week ${Math.ceil(delay/2)+1}-${delay}: Full recovery and stabilization`
        ];
        
      case 'ai-coding-impact':  // NEW WORKFLOW STEPS FOR AI CODING IMPACT
        const timeframe = analysis.input?.timeframe || '3-months';
        const recommendation = analysis.summary?.recommendation || 'maintain';
        
        if (recommendation === 'expand') {
          return [
            'Week 1: Identify high-impact teams for expansion',
            'Week 2: License procurement and allocation',
            'Week 3: Targeted training sessions',
            'Week 4: Initial adoption monitoring',
            'Month 2: Performance review and adjustments'
          ];
        } else if (recommendation === 'investigate') {
          return [
            'Week 1: Deep dive into underperforming areas',
            'Week 2: Developer interviews and feedback',
            'Week 3: Identify improvement opportunities',
            'Week 4: Create action plan',
            'Month 2: Implement and monitor changes'
          ];
        } else {
          return [
            'Month 1: Baseline usage monitoring',
            'Month 2: Optimization recommendations',
            'Month 3: Training material updates',
            'Quarterly: ROI analysis and reporting'
          ];
        }
        
      default:
        return ['Step 1: Analysis', 'Step 2: Planning', 'Step 3: Execution'];
    }
  };

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    onInputChange('');

    // Add user message
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, userMsg]);

    // Add loading message
    const loadingMsg: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      type: 'text',
      isLoading: true
    };

    setMessages(prev => [...prev, loadingMsg]);
    setIsLoading(true);

    // Simulate AI thinking
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Detect capability
    const detectedCapability = selectedCapability || detectCapabilityFromMessage(userMessage);
    
    // Process analysis
    const analysis = processAnalysis(detectedCapability || 'team-rebalancing', userMessage);
    
    // Generate enhanced response
    const aiResponse = generateEnhancedResponse(detectedCapability || 'team-rebalancing', analysis, userMessage);

    // Remove loading message and add AI response
    setMessages(prev => {
      const newMessages = prev.filter(msg => !msg.isLoading);
      return [
        ...newMessages,
        {
          id: Date.now().toString(),
          role: 'assistant',
          content: aiResponse,
          timestamp: new Date(),
          type: analysis ? 'analysis' : 'text',
          analysisData: analysis
        }
      ];
    });

    setIsLoading(false);
  };

  // Handle prompt suggestion click
  const handleSuggestionClick = (prompt: string) => {
    setInputValue(prompt);
    onInputChange(prompt);
    onPromptSelect(prompt);
  };

  // Handle key press (Enter to send)
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle view detailed analysis
  const handleViewDetailedAnalysis = (analysisData: any) => {
    setCurrentAnalysis(analysisData);
    setShowDetailedAnalysis(true);
    setAutoStartWorkflow(false);
    if (onViewDetailedAnalysis) {
      onViewDetailedAnalysis(analysisData);
    }
  };

  // Handle run workflow simulation
  const handleRunWorkflowSimulation = (analysisData: any) => {
    setCurrentAnalysis(analysisData);
    setShowDetailedAnalysis(true);
    setAutoStartWorkflow(true);
    if (onViewDetailedAnalysis) {
      onViewDetailedAnalysis(analysisData);
    }
  };

  // Format timestamp
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Render message content
  const renderMessageContent = (message: Message) => {
    if (message.isLoading) {
      return (
        <div className="flex items-center space-x-2 text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          <span>Analyzing with AI intelligence...</span>
        </div>
      );
    }

    // Parse markdown-like formatting
    const parseContent = (content: string) => {
      const lines = content.split('\n');
      const elements: JSX.Element[] = [];
      let currentList: string[] = [];
      let inList = false;

      const flushList = () => {
        if (currentList.length > 0) {
          elements.push(
            <ul key={`list-${elements.length}`} className="list-disc pl-5 mb-3 space-y-1">
              {currentList.map((item, idx) => (
                <li key={idx} className="text-gray-700">{item}</li>
              ))}
            </ul>
          );
          currentList = [];
        }
        inList = false;
      };

      lines.forEach((line, lineIndex) => {
        const trimmedLine = line.trim();
        
        // Skip empty lines
        if (trimmedLine === '') {
          if (inList) flushList();
          return;
        }

        // Handle headers
        if (trimmedLine.startsWith('## ')) {
          if (inList) flushList();
          elements.push(
            <h3 key={lineIndex} className="text-lg font-bold text-gray-900 mt-4 mb-3">
              {trimmedLine.replace('## ', '')}
            </h3>
          );
          return;
        }

        // Handle subheaders
        if (trimmedLine.startsWith('### ')) {
          if (inList) flushList();
          elements.push(
            <h4 key={lineIndex} className="text-md font-semibold text-gray-800 mt-3 mb-2">
              {trimmedLine.replace('### ', '')}
            </h4>
          );
          return;
        }

        // Handle bold text with **
        if (trimmedLine.includes('**')) {
          if (inList) flushList();
          const parts = trimmedLine.split('**');
          const formattedLine = parts.map((part, index) => 
            index % 2 === 1 ? <strong key={index} className="font-semibold">{part}</strong> : part
          );
          elements.push(
            <p key={lineIndex} className="mb-2 text-gray-800">
              {formattedLine}
            </p>
          );
          return;
        }

        // Handle list items
        if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ')) {
          inList = true;
          currentList.push(trimmedLine.substring(2));
          return;
        }

        // Handle numbered lists
        if (/^\d+\.\s/.test(trimmedLine)) {
          inList = true;
          currentList.push(trimmedLine.replace(/^\d+\.\s/, ''));
          return;
        }

        // Flush list if we were in one
        if (inList && !(trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ') || /^\d+\.\s/.test(trimmedLine))) {
          flushList();
        }

        // Handle regular paragraphs
        if (trimmedLine.startsWith('*Note:') || trimmedLine.startsWith('*Analysis')) {
          elements.push(
            <p key={lineIndex} className="text-xs text-gray-500 italic mt-2 mb-2">
              {trimmedLine.replace('*', '')}
            </p>
          );
        } else if (trimmedLine.includes('---')) {
          elements.push(<hr key={lineIndex} className="my-4 border-gray-200" />);
        } else {
          elements.push(
            <p key={lineIndex} className="mb-2 last:mb-0 text-gray-800">
              {trimmedLine}
            </p>
          );
        }
      });

      // Flush any remaining list
      flushList();

      return elements;
    };

    return (
      <div className="space-y-4">
        <div className="prose prose-sm max-w-none">
          {parseContent(message.content)}
        </div>
        
        {message.analysisData && message.type === 'analysis' && (
          <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
            <div className="flex items-center space-x-2 mb-3">
              <Sparkles size={16} className="text-blue-500" />
              <span className="text-sm font-medium text-blue-700">Intelligence Analysis Complete</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={() => handleViewDetailedAnalysis(message.analysisData)}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center space-x-1 bg-white py-2 rounded-lg border border-blue-200 hover:border-blue-300"
              >
                <ExternalLink size={14} />
                <span>View Detailed Analysis</span>
              </button>
              
              <button 
                onClick={() => handleRunWorkflowSimulation(message.analysisData)}
                className="text-sm text-green-600 hover:text-green-800 flex items-center justify-center space-x-1 bg-white py-2 rounded-lg border border-green-200 hover:border-green-300"
              >
                <Play size={14} />
                <span>Run Workflow</span>
              </button>
            </div>
          </div>
        )}
        
        {message.role === 'assistant' && !message.isLoading && (
          <div className="flex items-center space-x-4 pt-2 border-t border-gray-100">
            <button className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700">
              <Copy size={14} />
              <span>Copy</span>
            </button>
            <button className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700">
              <ThumbsUp size={14} />
              <span>Helpful</span>
            </button>
            <button className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700">
              <ThumbsDown size={14} />
              <span>Not helpful</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="h-full flex flex-col" ref={chatContainerRef}>
        {/* Messages Container - Fixed height with internal scrolling */}
        <div 
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-4 md:p-6"
          style={{ 
            minHeight: 0,
            maxHeight: 'calc(100vh - 200px)'
          }}
        >
          <div className="max-w-4xl mx-auto space-y-6">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-4 message-container ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                {/* Avatar */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  message.role === 'user' 
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500' 
                    : 'bg-gradient-to-r from-purple-500 to-pink-500'
                }`}>
                  {message.role === 'user' ? (
                    <User size={16} className="text-white" />
                  ) : (
                    <Bot size={16} className="text-white" />
                  )}
                </div>

                {/* Message Content */}
                <div className={`flex-1 ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block max-w-[85%] ${
                    message.role === 'user' 
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl rounded-tr-none' 
                      : 'bg-white border border-gray-200 rounded-2xl rounded-tl-none'
                  } p-4 shadow-sm`}>
                    {renderMessageContent(message)}
                  </div>
                  
                  {/* Timestamp */}
                  <div className={`mt-2 text-xs text-gray-500 ${message.role === 'user' ? 'text-right' : ''}`}>
                    {formatTime(message.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll to bottom button */}
        {showScrollToBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute right-6 bottom-24 p-2 bg-white border border-gray-300 rounded-full shadow-lg hover:shadow-xl hover:bg-gray-50 transition-all duration-200 z-10"
            style={{ position: 'fixed' }}
          >
            <ChevronDown size={18} className="text-gray-600" />
          </button>
        )}

        {/* Suggested Prompts - Compact section */}
        {suggestedPrompts.length > 0 && inputValue.length === 0 && (
          <div className="border-t bg-white px-4 py-2 flex-shrink-0">
            <div className="max-w-4xl mx-auto">
              <p className="text-xs text-gray-500 mb-1 flex items-center">
                <Sparkles size={12} className="mr-1" />
                Try asking:
              </p>
              <div className="flex flex-wrap gap-1">
                {suggestedPrompts.slice(0, 3).map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(prompt)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-xs text-gray-700 transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Input Area - Fixed at bottom */}
        <div className="border-t bg-white p-3 flex-shrink-0">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <textarea
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                placeholder="Ask about team dynamics, project bottlenecks, initiative planning, budget impacts, or AI coding tool effectiveness..."
                className="w-full p-3 pr-12 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none text-sm"
                rows={2}
                disabled={isLoading}
              />
              
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !inputValue.trim()}
                className={`absolute right-2 bottom-5 p-2 rounded-lg ${
                  isLoading || !inputValue.trim()
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'
                }`}
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
            
            <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
              <div className="flex items-center space-x-3">
                <span>AI Chief of Staff • Powered by Intelligence Algorithms</span>
              </div>
              <div className="flex items-center space-x-2">
                <button className="p-1 hover:bg-gray-100 rounded">
                  <Brain size={12} />
                </button>
                <button className="p-1 hover:bg-gray-100 rounded">
                  <Zap size={12} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analysis Modal */}
      {showDetailedAnalysis && currentAnalysis && (
        <DetailedAnalysis 
          analysisData={currentAnalysis} 
          capability={selectedCapability || 'general'} 
          onClose={() => {
            setShowDetailedAnalysis(false);
            setCurrentAnalysis(null);
            setAutoStartWorkflow(false);
          }}
          autoStartWorkflow={autoStartWorkflow}
        />
      )}
    </>
  );
};

export default ChatInterface;